import React, { useCallback, useEffect } from 'react';
import { ArrowBack } from '@mui/icons-material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Dialog,
  IconButton,
  Toolbar,
  Typography
} from '@mui/material';
import {
  SuprSendFeedProvider,
  useFeedClient,
  useFeedData
} from '@suprsend/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useSnackbar } from '@/hooks/useSnackbar';
import { Placeholder } from '@/pages/CRM/components/Placeholder';

import { mobileNotificationsOpenAtom } from './mobileNotificationsOpenAtom';
import NotificationCard from './NotificationCard';
import { notificationUnreadCountAtom } from './notificationUnreadCountAtom';

const MobileFeedSync: React.FC<{ onNewNotification: (data: any) => void }> = ({
  onNewNotification
}) => {
  const feedClient = useFeedClient();
  const feedData = useFeedData();
  const setUnreadCount = useSetAtom(notificationUnreadCountAtom);

  useEffect(() => {
    setUnreadCount(feedData?.meta?.badge ?? 0);
  }, [feedData, setUnreadCount]);

  useEffect(() => {
    if (!feedClient?.emitter) return;
    feedClient.emitter.on('feed.new_notification', onNewNotification);
    return () => {
      feedClient.emitter?.off('feed.new_notification', onNewNotification);
    };
  }, [feedClient, onNewNotification]);

  return null;
};

const MobileNotificationsDialog: React.FC = () => {
  const feedClient = useFeedClient();
  const feedData = useFeedData();
  const [open, setOpen] = useAtom(mobileNotificationsOpenAtom);
  const notifications = feedData?.notifications ?? [];
  useEffect(() => {
    if (open) {
      feedClient?.resetBadgeCount();
    }
  }, [open, feedClient]);

  return (
    <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: '#FCFEFF',
          boxShadow: 'none',
          borderBottom: '1px solid #E7EEF4'
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => setOpen(false)}>
            <ArrowBack htmlColor="#364955" />
          </IconButton>
          <Typography
            color="#0A1E28"
            fontWeight={500}
            fontSize="18px"
            sx={{ ml: 1 }}
          >
            Notifications
          </Typography>
          <Button
            onClick={() => feedClient?.markAllAsRead()}
            sx={{
              ml: 'auto',
              pr: 0,
              color: '#1DB8F2',
              fontWeight: 600,
              fontSize: '14px'
            }}
          >
            Mark all as read
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {notifications.length === 0 ? (
          <Placeholder
            Icon={NotificationsIcon}
            sx={{ textAlign: 'center', pt: 2 }}
            header="There are no unread notifications"
          />
        ) : (
          notifications.map((notification) => (
            <NotificationCard
              key={notification.n_id}
              notification={notification}
              onArchive={() => feedClient?.markAsArchived(notification.n_id)}
            />
          ))
        )}
      </Box>
    </Dialog>
  );
};

export const MobileNotificationsBell: React.FC = () => {
  const unreadCount = useAtomValue(notificationUnreadCountAtom);
  const setOpen = useSetAtom(mobileNotificationsOpenAtom);

  return (
    <IconButton onClick={() => setOpen(true)} size="small">
      <Badge
        badgeContent={unreadCount}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: '#1DB8F2',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            minWidth: '16px',
            height: '16px'
          }
        }}
      >
        <NotificationsIcon htmlColor="#364955" fontSize="small" />
      </Badge>
    </IconButton>
  );
};

export const MobileInboxWithNotifications: React.FC = () => {
  const { showSnackbar, closeSnackbar } = useSnackbar();

  const handleNewNotification = useCallback(
    (data: any) => {
      const key = showSnackbar({
        message: data.message.header,
        severity: 'info'
      });
      setTimeout(() => closeSnackbar(key), 6000);
    },
    [showSnackbar, closeSnackbar]
  );

  return (
    <SuprSendFeedProvider>
      <MobileFeedSync onNewNotification={handleNewNotification} />
      <MobileNotificationsDialog />
    </SuprSendFeedProvider>
  );
};
